import type {
  ProductionRequestLineRecord,
  ProductionRequestLineStatus,
  ProductionRequestStatus,
  ScheduleSlotRecord,
} from '../types/records'

export const REQUEST_LINE_STATUS_LABELS: Record<ProductionRequestLineStatus, string> = {
  pending: 'Ожидает',
  scheduled: 'Запланировано',
  in_progress: 'В работе',
  completed: 'Выполнено',
  shipped: 'Отгружено',
  cancelled: 'Отменено',
}

export function requestLineStatusLabel(status: ProductionRequestLineStatus): string {
  return REQUEST_LINE_STATUS_LABELS[status] ?? status
}

export const REQUEST_STATUS_LABELS: Record<ProductionRequestStatus, string> = {
  draft: 'Черновик',
  planning: 'Планирование',
  in_progress: 'В работе',
  completed: 'Завершена',
  cancelled: 'Отменена',
}

export function requestStatusLabel(status: ProductionRequestStatus | string | undefined): string {
  if (!status) return '—'
  return REQUEST_STATUS_LABELS[status as ProductionRequestStatus] ?? status
}

export function lineStatusLabel(status: ProductionRequestLineStatus | string | undefined): string {
  if (!status) return '—'
  return requestLineStatusLabel(status as ProductionRequestLineStatus)
}

const REQUEST_BADGE: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-700',
  planning: 'bg-indigo-100 text-indigo-800',
  in_progress: 'bg-emerald-100 text-emerald-800',
  completed: 'bg-slate-200 text-slate-700',
  cancelled: 'bg-red-100 text-red-800',
}

const LINE_BADGE: Record<string, string> = {
  pending: 'bg-slate-100 text-slate-700',
  scheduled: 'bg-indigo-100 text-indigo-800',
  in_progress: 'bg-emerald-100 text-emerald-800',
  completed: 'bg-slate-200 text-slate-700',
  shipped: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-red-100 text-red-800',
}

export function statusBadgeClass(
  kind: 'request' | 'line',
  status: string | undefined,
): string {
  const base = 'inline-flex rounded-full px-2 py-0.5 text-xs font-semibold'
  const map = kind === 'request' ? REQUEST_BADGE : LINE_BADGE
  return `${base} ${map[status ?? ''] ?? 'bg-slate-100 text-slate-600'}`
}

export function activeSlots(slots: ScheduleSlotRecord[]): ScheduleSlotRecord[] {
  return slots.filter((s) => s.status !== 'cancelled')
}

export function scheduledQuantitySum(slots: ScheduleSlotRecord[]): number {
  return activeSlots(slots).reduce((sum, s) => sum + (Number(s.plannedQuantity) || 0), 0)
}

export function producedQuantitySum(slots: ScheduleSlotRecord[]): number {
  return activeSlots(slots).reduce((sum, s) => sum + (Number(s.actualQuantity) || 0), 0)
}

export function remainingToSchedule(
  line: Pick<ProductionRequestLineRecord, 'quantity'>,
  slots: ScheduleSlotRecord[],
): number {
  const qty = Number(line.quantity) || 0
  return Math.max(0, qty - scheduledQuantitySum(slots))
}

export function isQuantityExceeded(
  line: Pick<ProductionRequestLineRecord, 'quantity'>,
  slots: ScheduleSlotRecord[],
): boolean {
  return scheduledQuantitySum(slots) > (Number(line.quantity) || 0)
}

export function computeLineStatus(
  line: Pick<ProductionRequestLineRecord, 'quantity' | 'shippedQty'>,
  slots: ScheduleSlotRecord[],
): ProductionRequestLineStatus {
  const qty = Number(line.quantity) || 0
  const shipped = Number(line.shippedQty) || 0
  if (qty > 0 && shipped >= qty) return 'shipped'

  const active = activeSlots(slots)
  if (active.length === 0) return 'pending'

  if (active.some((s) => s.status === 'in_progress')) return 'in_progress'

  const plannedSum = scheduledQuantitySum(slots)
  const produced = producedQuantitySum(slots)
  const allCompleted = active.every((s) => s.status === 'completed')

  if (produced >= qty || (allCompleted && plannedSum >= qty)) return 'completed'
  if (plannedSum >= qty && active.every((s) => s.status === 'planned')) return 'scheduled'

  return 'pending'
}

export function isLineOverdue(
  line: Pick<ProductionRequestLineRecord, 'plannedProduction' | 'status'>,
  today: string = new Date().toISOString().slice(0, 10),
): boolean {
  if (line.status === 'completed' || line.status === 'shipped' || line.status === 'cancelled') {
    return false
  }
  const d = line.plannedProduction?.slice(0, 10)
  if (!d) return false
  return d < today
}

export function rollupRequestStatus(
  lines: Pick<ProductionRequestLineRecord, 'status'>[],
): ProductionRequestStatus {
  if (lines.length === 0) return 'draft'

  const statuses = lines.map((l) => l.status)
  if (statuses.every((s) => s === 'cancelled')) return 'cancelled'
  if (statuses.every((s) => s === 'shipped' || s === 'cancelled')) return 'completed'
  if (
    statuses.every((s) => s === 'completed' || s === 'shipped' || s === 'cancelled')
  ) {
    return 'completed'
  }
  if (statuses.some((s) => s === 'in_progress')) return 'in_progress'
  if (statuses.some((s) => s === 'scheduled')) return 'planning'
  if (statuses.some((s) => s === 'pending')) return 'planning'
  return 'draft'
}
